import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NavBar, SideNav, Breadcrumb } from "../../components";
import { Button, Modal } from "flowbite-react";
import {
  fetchPosts,
  deletePost,
  setSelectedPost,
  storeCurrentTab,
} from "../../redux/slices/posts";
import { timestampToTimeAgo } from "../../config/firebase";
import { FiTrash2, FiCopy, FiEye, FiList } from "react-icons/fi";
import { AiOutlineLoading } from "react-icons/ai";
import { HiOutlineExclamationCircle } from "react-icons/hi";

const DRAFTS = "drafts";
const POSTS = "posts";

const ListPost = ({ category }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const postsStore = useSelector((state) => state.posts);
  const { user } = useSelector((state) => state.auth);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);

  useEffect(() => {
    const didLoad = async () => {
      dispatch(
        fetchPosts({ category, isDraft: postsStore.currentTab === DRAFTS })
      );
    };

    didLoad();
  }, [dispatch, category, postsStore.currentTab]);

  const loadMoreContent = async (e) => {
    e.preventDefault();
    const lastDocId = postsStore[category][postsStore.currentTab].lastDocId;
    dispatch(
      fetchPosts({
        category,
        isDraft: postsStore.currentTab === DRAFTS,
        lastDocId,
      })
    );
  };

  const handleDeletePost = async (id) => {
    try {
      const resultAction = await dispatch(
        deletePost({ isDraft: postsStore.currentTab === DRAFTS, id, category })
      );
      if (deletePost.fulfilled.match(resultAction)) {
        setOpenDeleteModal(false);
      }
    } catch {}
  };

  const handleViewPost = (post) => {
    dispatch(setSelectedPost(post));
    const isDraft =
      postsStore.currentTab === DRAFTS || post.isDraft === true ? "true" : "false";
    navigate(`/${category}/${post.id}?isDraft=${isDraft}`);
  };

  const handleViewProgrammes = (post) => {
    dispatch(setSelectedPost(post));
    const isDraft =
      postsStore.currentTab === DRAFTS || post.isDraft === true ? "true" : "false";
    navigate(
      `/${category}/${post.id}/programmes?isDraft=${isDraft}&school=${post.title}`
    );
  };

  const Post = ({ row, white }) => {
    return (
      <tr
        className={`border-b ${
          white !== true ? "bg-white" : "bg-gray-50"
        } dark:bg-gray-800 dark:border-gray-700`}
      >
        <th
          scope="row"
          className="flex items-center px-6 py-4 text-gray-900 whitespace-nowrap dark:text-white"
        >
          <img
            alt="Thumbnail"
            src={row.thumbnail}
            className="w-[40px] h-[40px] rounded bg-gray-200 dark:bg-gray-700"
          />
          <td className="px-6 py-4 text-lg text-black dark:text-white">
            {row.title}
          </td>
        </th>
        <td className="px-6 py-4">
          {row.author?.fullName ?? "—"}
        </td>
        <td className="px-6 py-4">{timestampToTimeAgo(row.createdAt)}</td>
        <td className="px-6 py-4 flex gap-4">
          <Button
            color="purple"
            size="sm"
            className="relative flex items-center justify-center group"
            onClick={() => handleViewPost(row)}
          >
            <FiEye className="h-5 w-5" />
            <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded">
              View
            </span>
          </Button>

          <Button
            color="blue"
            size="sm"
            className="relative flex items-center justify-center group"
            onClick={() => {
              const clone = { ...row, id: "clone" };
              handleViewPost(clone);
            }}
          >
            <FiCopy className="h-5 w-5" />
            <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded">
              Duplicate
            </span>
          </Button>

          {row.category === "school" && (
            <Button
              color="green"
              size="sm"
              className="relative flex items-center justify-center group"
              onClick={() => handleViewProgrammes(row)}
            >
              <FiList className="h-5 w-5" />
              <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded">
                View Programmes
              </span>
            </Button>
          )}

          {user &&
            (user.userType === "admin" ||
              (row.author && user.id === row.author.id)) && (
            <Button
              color="red"
              size="sm"
              className="relative flex items-center justify-center group"
              onClick={() => {
                setSelectedPostId(row.id);
                setOpenDeleteModal(true);
              }}
            >
              <FiTrash2 className="h-5 w-5" />
              <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black text-white px-2 py-1 rounded">
                Delete
              </span>
            </Button>
          )}
        </td>
      </tr>
    );
  };

  const currentPosts = postsStore[category][postsStore.currentTab];
  const list = currentPosts.data.map((item, index) => (
    <Post row={item} white={index % 2 === 0} />
  ));

  const activeTabStyle =
    "inline-block p-4 text-blue-600 border-b-2 border-blue-600 rounded-t-lg active dark:text-blue-500 dark:border-blue-500";
  const inactiveTabStyle =
    "inline-block p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300";

  return (
    <>
      <div className="antialiased bg-gray-50 dark:bg-gray-900">
        <NavBar />
        <SideNav />
        <Modal
          show={openDeleteModal}
          size="md"
          onClose={() => setOpenDeleteModal(false)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="text-center">
              <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                {category === "school"
                  ? "Delete this school and all of its programmes?"
                  : "Are you sure you want to delete this post?"}
              </h3>
              {category === "school" && (
                <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                  This also permanently removes published and draft programmes
                  linked to the school, plus related review change sets.
                </p>
              )}
              <div className="flex justify-center gap-4">
                <Button
                  color="failure"
                  onClick={() => handleDeletePost(selectedPostId)}
                  disabled={postsStore.actionLoading}
                  isProcessing={postsStore.actionLoading}
                  processingSpinner={
                    <AiOutlineLoading className="h-6 w-6 animate-spin" />
                  }
                >
                  {"Yes, I'm sure"}
                </Button>
                <Button color="gray" onClick={() => setOpenDeleteModal(false)}>
                  No, cancel
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <main className="p-4 md:ml-64 h-auto pt-20">
          <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
            <Breadcrumb />
            <div class="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
              <ul class="flex flex-wrap -mb-px">
                <li class="me-2">
                  <button
                    class={
                      postsStore.currentTab === POSTS
                        ? activeTabStyle
                        : inactiveTabStyle
                    }
                    aria-current="page"
                    onClick={(e) => {
                      dispatch(storeCurrentTab(POSTS));
                    }}
                  >
                    Posts
                  </button>
                </li>
                <li class="me-2">
                  <button
                    class={
                      postsStore.currentTab === DRAFTS
                        ? activeTabStyle
                        : inactiveTabStyle
                    }
                    onClick={(e) => {
                      dispatch(storeCurrentTab(DRAFTS));
                    }}
                  >
                    Drafts
                  </button>
                </li>
                <li class="me-2">
                  <Link to={`/${category}/new`} c class={inactiveTabStyle}>
                    Compose
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          {postsStore.pageLoading && list.length === 0 ? (
            // Skeleton Loader
            <div className="animate-puls mt-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-4 p-4">
                <div className="grid grid-cols-4">
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <hr className="mt-2" />
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div
                      alt="Thumbnail"
                      className="w-[40px] h-[40px] rounded mr-4 bg-gray-200 dark:bg-gray-700"
                    />
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div
                      alt="Thumbnail"
                      className="w-[40px] h-[40px] rounded mr-4 bg-gray-200 dark:bg-gray-700"
                    />
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div
                      alt="Thumbnail"
                      className="w-[40px] h-[40px] rounded mr-4 bg-gray-200 dark:bg-gray-700"
                    />
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div
                      alt="Thumbnail"
                      className="w-[40px] h-[40px] rounded mr-4 bg-gray-200 dark:bg-gray-700"
                    />
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
                <div className="grid grid-cols-4 mt-4 items-center">
                  <div className="flex">
                    <div
                      alt="Thumbnail"
                      className="w-[40px] h-[40px] rounded mr-4 bg-gray-200 dark:bg-gray-700"
                    />
                    <div>
                      <div className="mt-2 h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-4">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-600 bg-white uppercase  dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Title
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Author
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Created At
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>{list}</tbody>
                </table>
              </div>
              <div className="flex justify-center mt-4">
                {postsStore[category][postsStore.currentTab].hasMoreContent && (
                  <Button
                    size="sm"
                    color="gray"
                    onClick={loadMoreContent}
                    disabled={postsStore.pageLoading}
                    isProcessing={postsStore.pageLoading}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                  >
                    Load More
                  </Button>
                )}
              </div>
              {postsStore[category][postsStore.currentTab].data.length ===
                0 && (
                <center className="text-gray-400 italic">
                  No {category} available
                </center>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default ListPost;
